import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Empty, Flex, Skeleton } from 'antd';
import { useIntl } from 'react-intl';
import TransactionsList from './TransactionsList';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { Transaction, TransactionDetails } from 'widgets/transactions/model/types';
import { debounce } from 'lodash';
import { getErrorDefaultMessage } from 'shared/libs/helpers';
import Result from 'shared/ui/Result';
import { CloseOutlined } from '@ant-design/icons';
import TransactionDetailsModal from './TransactionDetails';
import Filters from '../components/Filters';
import { groupTransactions } from '../model/utils';
import { MonthSpending } from '../components/MonthSpending';
import { ALL_CONFIG, TRANSACTION_COUNT } from '../model/constants';
import {
  useGetFiltersQuery,
  useLazyGetTransactionByIdQuery,
  useLazyGetTransactionsQuery
} from '../api';
import { GetTransactionsParams } from '../api/dto';
import Search from '../components/Search';
import moment from 'moment';

type DrawerTransactionsProps = {
  accountId: number;
  open: boolean;
  onClose: () => void;
};

const DrawerTransactions: React.FC<DrawerTransactionsProps> = ({ accountId, open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const [fetchTransactions, { data, error, isError, isLoading, isFetching, isSuccess }] =
    useLazyGetTransactionsQuery();
  const [query, setQuery] = useState('');
  const { data: filtersData } = useGetFiltersQuery({ accountId });
  const config = useRef<GetTransactionsParams>(ALL_CONFIG);
  const currentMonthStart = moment().startOf('month').format('DD-MM-YYYY');
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true);
  const [activeTransaction, setActiveTransaction] = useState<Transaction>();
  const [activeTransactionDetails, setActiveTransactionDetails] = useState<TransactionDetails>();
  const { showError } = useMessage();
  const [getTransactionById] = useLazyGetTransactionByIdQuery();
  const [transactionLoading, setTransactionLoading] = useState(false);
  const previousAccountId = useRef<number | null>(null);

  const onFetchTransactions = useCallback(() => {
    const currentPage = config.current.page;
    fetchTransactions({
      accountId: accountId,
      params: config.current,
      merge: config.current.page !== 0
    })
      .unwrap()
      .then(({ response }) => {
        // Check if there are more pages after the current one
        if (currentPage >= Number(response.totalPages) - 1) {
          setCanLoadMore(false);
        } else {
          setCanLoadMore(true);
        }

        // Always increment page for next fetch after successful load
        config.current = {
          ...config.current,
          page: currentPage + 1
        };
      });
  }, [accountId, fetchTransactions]);

  // Reset and reload transactions when accountId changes or drawer opens
  useEffect(() => {
    if (open && (previousAccountId.current === null || previousAccountId.current !== accountId)) {
      // Reset config when accountId changes
      if (previousAccountId.current !== null && previousAccountId.current !== accountId) {
        config.current = { ...ALL_CONFIG };
        setQuery('');
        setCanLoadMore(true);
        setActiveTransaction(undefined);
        setActiveTransactionDetails(undefined);
      }
      previousAccountId.current = accountId;
      onFetchTransactions();
    }
  }, [accountId, open, onFetchTransactions]);

  const handleDateRangeChange = useCallback((startDate: string, endDate: string | undefined) => {
    setCanLoadMore(true);
    config.current = {
      ...config.current,
      start: startDate,
      end: endDate || moment().endOf('day').format('DD-MM-YYYY'),
      page: 0
    };
    onFetchTransactions();
  }, [onFetchTransactions]);

  const debouncedFetchTransactions = useCallback(
    debounce(() => onFetchTransactions(), 500),
    [onFetchTransactions]
  );

  const onChangeQuery = (input: string) => {
    setQuery(input);
    config.current = {
      ...config.current,
      page: 0,
      query: input
    };

    debouncedFetchTransactions();
  };

  const renderFilters = () => {
    if (filtersData?.response)
      return (
        <Filters
          filters={filtersData?.response.filters}
          onSelect={(type) => {
            const newType = type === 'All' || !type ? undefined : type;
            config.current = {
              ...config.current,
              filter: newType,
              page: 0
            };

            onFetchTransactions();
          }}
        />
      );
  };

  const onClickTransaction = (transaction: Transaction) => {
    setActiveTransaction(transaction);
    setTransactionLoading(true);
    getTransactionById({
      accountId,
      transactionId: transaction.id
    })
      .unwrap()
      .then((response) => {
        setActiveTransactionDetails(response.response);
      })
      .catch(() => {
        showError(t({ id: 'common.message.error' }));
      })
      .finally(() => {
        setTransactionLoading(false);
      });
  };

  const renderList = () => {
    if (isError) {
      return (
        <Flex vertical align='center' gap={16} style={{ width: '100%', marginTop: 32 }}>
          <Result
            type='error'
            title={t({ id: 'drawer.transactions.wrong' })}
            subtitle={getErrorDefaultMessage(error)}
          />
          <Button
            onClick={() => onFetchTransactions()}
            type='primary'
            style={{ backgroundColor: 'black' }}
          >
            {t({ id: 'drawer.transactions.refresh' })}
          </Button>
        </Flex>
      );
    }

    if (isLoading) {
      return (
        <Flex gap={16} vertical style={{ width: '100%' }}>
          <Search value={query} onChange={onChangeQuery} />
          <Flex>{renderFilters()}</Flex>
          <MonthSpending 
            accountId={accountId} 
            start={currentMonthStart}
            onDateRangeChange={handleDateRangeChange}
          />
          <Skeleton active />
        </Flex>
      );
    }

    if (isSuccess) {
      if (!data) return;
      const transactionsList = data.response.transactions;
      const groupedData = groupTransactions(transactionsList);

      return (
        <Flex vertical style={{ width: '100%' }}>
          <Flex gap={16} vertical>
            <Search value={query} onChange={onChangeQuery} />
            <Flex>{renderFilters()}</Flex>
            <MonthSpending 
            accountId={accountId} 
            start={currentMonthStart}
            onDateRangeChange={handleDateRangeChange}
          />
            <Skeleton loading={isLoading || isFetching}>
              {transactionsList.length ? (
                <TransactionsList
                  transactions={groupedData}
                  onClickTransaction={onClickTransaction}
                />
              ) : (
                <Flex vertical align='center' style={{ width: '100%' }}>
                  <Empty description={t({ id: 'drawer.transactions.notFound' })} />
                </Flex>
              )}
            </Skeleton>
            {canLoadMore && transactionsList.length >= TRANSACTION_COUNT && (
              <div style={{ paddingInline: 16 }}>
                <Button
                  style={{ marginBottom: 32, backgroundColor: 'black', color: 'white' }}
                  disabled={isFetching}
                  onClick={() => onFetchTransactions()}
                >
                  {t({ id: 'buttons.loadMore' })}
                </Button>
              </div>
            )}
          </Flex>
        </Flex>
      );
    }
  };

  return (
    <Drawer
      closeIcon={<CloseOutlined id='btn__transactions-close' />}
      title={t({ id: 'transactions.title' })}
      open={open}
      onClose={onClose}
      width={560}
    >
      <Flex style={{ marginBottom: 32 }} justify='space-between'>
        {renderList()}
      </Flex>
      <TransactionDetailsModal
        transaction={activeTransaction}
        transactionDetails={activeTransactionDetails}
        onClose={() => setActiveTransaction(undefined)}
        loading={transactionLoading}
      />
    </Drawer>
  );
};

export default DrawerTransactions;
